// Shared presentation metadata for the Longevity report dashboard.
// Pure data + formatting — the scoring itself is derived server-side in
// `backend/longevity/report.py` from the data already stored in the vault.
import {
  Droplet, PersonStanding, Dumbbell, Brain, Apple, Moon, Sparkles,
} from "lucide-react";

export const AREA_META = {
  blood_biomarkers: {
    index: 1,
    icon: Droplet,
    tint: "#b4474d",
    image: "https://images.unsplash.com/photo-1639772823849-6efbd173043c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    // Which existing vault card the guest taps to enter this data.
    anchor: "biometric-card-blood",
  },
  functional_age: {
    index: 2,
    icon: PersonStanding,
    tint: "#4a7fa6",
    image: "https://images.pexels.com/photos/30564728/pexels-photo-30564728.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=600",
    anchor: "biometric-card-body",
  },
  grip_strength: {
    index: 3,
    icon: Dumbbell,
    tint: "#c07b36",
    image: "https://images.unsplash.com/photo-1556232503-2c7aca160281?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    anchor: "biometric-card-body",
  },
  cognitive_performance: {
    index: 4,
    icon: Brain,
    tint: "#6c5ea8",
    image: "https://images.unsplash.com/photo-1617791160536-598cf32026fb?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    anchor: "biometric-card-sleep",
  },
  lifestyle_score: {
    index: 5,
    icon: Apple,
    tint: "#5d8a5a",
    image: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    anchor: "longevity-onboarding-cta-card",
  },
  recovery_sleep: {
    index: 6,
    icon: Moon,
    tint: "#4b5f8c",
    image: "https://images.pexels.com/photos/7445173/pexels-photo-7445173.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=600",
    anchor: "biometric-card-sleep",
  },
  skin_analysis: {
    index: 7,
    icon: Sparkles,
    tint: "#b67358",
    image: "https://images.unsplash.com/photo-1675773051474-55c4b7d2cf53?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    anchor: "biometric-card-bio-age",
  },
};

export const BAND_COLORS = {
  Excellent: { fg: "#1f6b4d", bg: "#dff0e6", ring: "#2e8b63" },
  Good: { fg: "#7a6213", bg: "#fbf0c9", ring: "#b89a2a" },
  Fair: { fg: "#8a5a1c", bg: "#fae3cc", ring: "#c07b36" },
  Poor: { fg: "#8f2f2f", bg: "#f8dada", ring: "#b4474d" },
  "Awaiting data": { fg: "#6b6b63", bg: "#ececea", ring: "#b5b5ad" },
};

export const bandColor = (band) => BAND_COLORS[band] || BAND_COLORS["Awaiting data"];

export const PROGRESS_TABS = [
  { key: "overall", label: "Overall Score", dataKey: "overall", color: "#2e8b63" },
  { key: "functional", label: "Functional Age", dataKey: "functional_age_years", color: "#4a7fa6" },
  { key: "brain", label: "Brain Age", dataKey: "brain_age_years", color: "#6c5ea8" },
  { key: "biomarkers", label: "Key Biomarkers", dataKey: "key_biomarkers", color: "#b4474d" },
];

export function prettyDate(ymd) {
  if (!ymd) return "—";
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
}

export function shortMonth(ymd) {
  if (!ymd) return "—";
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit", timeZone: "UTC" });
}

// Scroll the guest to the existing vault card that owns this data. The
// data-entry interaction itself is untouched — we only bring it into view.
export function focusVaultCard(anchor) {
  const el = document.querySelector(`[data-testid="${anchor}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("ring-2", "ring-primary", "ring-offset-2");
  setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-offset-2"), 2200);
}
